import { useNavigate } from 'react-router-dom'
import { useTheme } from '../components/ThemeProvider'

const Terms = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-white/60 dark:bg-sage-800/60 border-b border-sage-100/30 dark:border-sage-700/30">
        <button
          onClick={() => navigate('/home')}
          className="p-3 rounded-2xl hover:bg-sage-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
        >
          <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-light text-sage-600 dark:text-sage-300">Terms of Use</h1>
        <div className="w-11"></div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Last Updated */}
        <div className="text-center mb-8">
          <p className="text-sage-400 dark:text-sage-500 text-sm font-light">
            Last updated: January 2025
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-8">
          <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm">
            <h2 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-4">Welcome to Mind Polish</h2>
            <p className="text-sage-500 dark:text-sage-400 font-light leading-relaxed">
              These terms outline how you can use Mind Polish safely and respectfully. We've written them in plain language because we believe legal documents should be accessible to everyone.
            </p>
          </div>
        </section>

        {/* Acceptable Use */}
        <section className="mb-8">
          <div className="bg-mint-50/60 dark:bg-sage-800/60 rounded-3xl border border-mint-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-mint-600 dark:text-mint-300 font-light text-base sm:text-lg mb-6">How to Use Mind Polish Respectfully</h3>
            <div className="space-y-4">
              <p className="text-mint-500 dark:text-mint-400 font-light leading-relaxed">
                Mind Polish is designed to be a safe, supportive space for everyone. To maintain this environment, we ask that you:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Use the app for personal wellness and self-care purposes
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Respect the privacy and safety of the community
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Avoid sharing harmful, abusive, or inappropriate content
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Don't attempt to misuse or exploit the app's features
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Age Requirements */}
        <section className="mb-8">
          <div className="bg-cream-50/60 dark:bg-sage-800/60 rounded-3xl border border-cream-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-cream-600 dark:text-cream-300 font-light text-base sm:text-lg mb-6">Age and Capacity</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-2">Recommended Ages</h4>
                <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm leading-relaxed">
                  Mind Polish is designed for users 13 and older. We recommend users 16 and older for full access to all features, including AI conversations with Dopa.
                </p>
              </div>
              
              <div>
                <h4 className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-2">Parental Guidance</h4>
                <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm leading-relaxed">
                  If you're under 18, we encourage you to discuss your use of mental wellness apps with a parent, guardian, or trusted adult.
                </p>
              </div>
              
              <div>
                <h4 className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-2">Capacity to Agree</h4>
                <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm leading-relaxed">
                  By using Mind Polish, you confirm that you have the legal capacity to enter into these terms in your jurisdiction.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Liability Limitations */}
        <section className="mb-8">
          <div className="bg-peach-50/60 dark:bg-sage-800/60 rounded-3xl border border-peach-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-peach-600 dark:text-peach-300 font-light text-base sm:text-lg mb-6">Our Responsibilities and Limitations</h3>
            <div className="space-y-4">
              <p className="text-peach-500 dark:text-peach-400 font-light leading-relaxed">
                We work hard to provide a reliable, helpful service, but we want to be clear about what we can and cannot guarantee.
              </p>
              
              <div>
                <h4 className="text-peach-600 dark:text-peach-300 font-light text-sm sm:text-base mb-2">Service Availability</h4>
                <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
                  While we strive for 99.9% uptime, we cannot guarantee that Mind Polish will always be available. Technical issues, maintenance, or other factors may occasionally interrupt service.
                </p>
              </div>
              
              <div>
                <h4 className="text-peach-600 dark:text-peach-300 font-light text-sm sm:text-base mb-2">AI Responses</h4>
                <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
                  Dopa's responses are generated by AI and may not always be perfect or appropriate for every situation. We cannot be held responsible for AI-generated content.
                </p>
              </div>
              
              <div>
                <h4 className="text-peach-600 dark:text-peach-300 font-light text-sm sm:text-base mb-2">Personal Decisions</h4>
                <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
                  You are responsible for your own decisions and actions. Mind Polish provides tools and support, but cannot be held liable for outcomes based on your use of the app.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Data and Privacy */}
        <section className="mb-8">
          <div className="bg-sage-50/60 dark:bg-sage-800/60 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-6">Your Data and Content</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-sage-600 dark:text-sage-300 font-light text-sm sm:text-base mb-2">Ownership</h4>
                <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                  You own all the content you create in Mind Polish - your journal entries, reflections, and personal data remain yours.
                </p>
              </div>
              
              <div>
                <h4 className="text-sage-600 dark:text-sage-300 font-light text-sm sm:text-base mb-2">Privacy</h4>
                <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                  We handle your data according to our Privacy Policy. We never sell your personal information or use it for advertising.
                </p>
              </div>
              
              <div>
                <h4 className="text-sage-600 dark:text-sage-300 font-light text-sm sm:text-base mb-2">Backup Responsibility</h4>
                <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                  While we maintain backups, we encourage you to export your data regularly. You're responsible for keeping your own copies of important content.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Changes to Terms */}
        <section className="mb-8">
          <div className="bg-neutral-50/60 dark:bg-sage-800/60 rounded-3xl border border-neutral-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-neutral-600 dark:text-neutral-300 font-light text-base sm:text-lg mb-6">Changes to These Terms</h3>
            <div className="space-y-4">
              <p className="text-neutral-500 dark:text-neutral-400 font-light leading-relaxed">
                We may update these terms occasionally to reflect changes in our service or legal requirements. When we do:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-neutral-400 dark:bg-neutral-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm leading-relaxed">
                    We'll update the "Last updated" date at the top of this page
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-neutral-400 dark:bg-neutral-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm leading-relaxed">
                    For significant changes, we'll notify you through the app or via email
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-neutral-400 dark:bg-neutral-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm leading-relaxed">
                    Continued use of the app means you accept the updated terms
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section>
          <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm text-center">
            <h3 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg mb-4">Questions About These Terms?</h3>
            <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed mb-6">
              We're happy to clarify anything that seems unclear. Legal language can be confusing, and we want you to feel informed.
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-sage-100/60 hover:bg-sage-200/50 
                         dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                         text-sage-600 dark:text-sage-300 rounded-2xl text-xs sm:text-sm font-light 
                         border border-sage-200/40 dark:border-sage-600/40 
                         hover:border-sage-300/50 dark:hover:border-sage-500/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
            >
              Ask About Terms
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Terms